import React, { useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { FormikProps } from 'formik'
import { useApolloClient } from '@apollo/client'
import { Stripe, StripeCardElement } from '@stripe/stripe-js'
import { useStripe, useElements as useStripeElements, CardElement } from '@stripe/react-stripe-js'
import {
  AddressInput,
  ValidateTestLocationAddress,
  ValidateTestLocationAddressVariables,
  UserFormValues,
  useAuth,
  FormStep,
  useRequestTestSession,
  INITIAL_APPLICANTS_COUNT,
  useCardForm,
  CardFieldName,
  useFinishTestSessionPayment,
  VALIDATE_TEST_LOCATION_ADDRESS,
  CardFormValues,
  IPaymentCard,
  CategoryType,
  AddressFieldName,
} from '@modmd/data'
import { DEVICE, pxToRem } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { ROUTES } from 'client-portal/constants/routes'
import Button from 'components/Button'
import { Stepper } from 'components/Stepper'
import { PageLayout, PersonalPageLayout } from 'client-portal/components/PageLayout'
import { Payment } from 'sharedComponents/Payment'
import { useToaster } from 'utils/useToaster'
import { UserInformation } from './UserInformation'
import { PretestSurvey } from './PretestSurvey'
import { Summary } from './Summary'
import { TestSelection } from './TestSelection'
import { AdditionalPatients } from './AdditionalPatients'
import { ThankYou } from './ThankYou'
import { DetailPayment } from './DetailPayment'

interface StepModel {
  path: FormStep
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forms: FormikProps<any>[]
  goNext: () => void
}

interface RouterProps {
  formStep: FormStep
}

type TestSessionDetailsProps = RouteComponentProps<RouterProps>

export interface InsuranceProps {
  isInsurance: boolean
  insuranceId: string | null
}

export const RequestTestSession: React.VFC<TestSessionDetailsProps> = ({ match, history }) => {
  const stripe = useStripe()
  const elements = useStripeElements()
  const { setToastMessage } = useToaster()
  const [isFetching, setIsFetching] = React.useState(false)
  const [isWalkin, setIsWalkin] = React.useState(false)
  const [emailAlreadyExist, setEmailAlreadyExist] = React.useState<string>()

  const auth = useAuth()
  const {
    userForm,
    testSessionForm,
    symptomsForm,
    selectTestForm,
    previousExposureForm,
    isRequestSessionLoading,
    couponForm,
    insuranceForm,
    setInsuranceForm,
    insuranceFormNewUser,
  } = useRequestTestSession({
    isNewUser: !auth.isLoggedIn,
    isWalkin,
  })

  const [
    finishTestSessionPayment,
    { loading: isFinishSessionLoading },
  ] = useFinishTestSessionPayment()
  const [labAddress, setLabAddress] = React.useState({})

  const confirmPayment = async (
    stripe: Stripe,
    paymentSecret: string,
    paymentMethod: string | { card: StripeCardElement; billing_details: { name: string } },
    selectedCard: IPaymentCard | null,
    values: CardFormValues
  ) => {
    await stripe
      .confirmCardPayment(paymentSecret, {
        payment_method: paymentMethod,
        setup_future_usage:
          !selectedCard && values[CardFieldName.IS_SAVE_CARD] ? 'off_session' : undefined,
      })
      .then(async (stripeResult) => {
        if (stripeResult.error) {
          setToastMessage(stripeResult.error.message as string, 'danger')
          await finishTestSessionPayment({
            variables: {
              stripePaymentId: stripeResult.error.payment_intent!.id,
              paymentError: stripeResult.error.code,
            },
          })
        } else {
          const stripePaymentId = stripeResult?.paymentIntent?.id
          await finishTestSessionPayment({ variables: { stripePaymentId } })
        }
      })
  }

  const cardForm = useCardForm({
    onSubmit: async (values) => {
      if (!stripe || !elements) {
        return false
      }
      try {
        let paymentMethod: string | { card: StripeCardElement; billing_details: { name: string } }
        const selectedCard = values[CardFieldName.SELECTED_CARD]
        if (selectedCard) {
          paymentMethod = selectedCard.stripeId
        } else {
          paymentMethod = {
            card: elements.getElement(CardElement) as StripeCardElement,
            billing_details: {
              name: `${userForm.values.firstName!} ${userForm.values.lastName!}`,
            },
          }
        }
        if (testSessionForm.values.paymentSecret && testSessionForm.values.paymentSecret[0])
          await confirmPayment(
            stripe,
            testSessionForm.values.paymentSecret[0],
            paymentMethod,
            selectedCard,
            values
          )
        if (testSessionForm.values.paymentSecret && testSessionForm.values.paymentSecret[1])
          await confirmPayment(
            stripe,
            testSessionForm.values.paymentSecret[1],
            paymentMethod,
            selectedCard,
            values
          )
        if (testSessionForm.values.paymentSecret && testSessionForm.values.paymentSecret[2])
          await confirmPayment(
            stripe,
            testSessionForm.values.paymentSecret[2],
            paymentMethod,
            selectedCard,
            values
          )
        return true
      } catch (error) {
        // ignore error
      }
      return false
    },
  })

  const loggedInUserId = auth.data.User.id
  const [testLocationId, setTestLocationId] = useState<null | string>(null)
  const [addressNotCovered, setAddressNotCovered] = useState(false)
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const { formStep } = match.params
  const apolloClient = useApolloClient()

  const steps: StepModel[] = [
    {
      path: FormStep.PretestSurvey,
      forms:
        testSessionForm.values.categoryType === CategoryType.Covid19
          ? [symptomsForm, testSessionForm, previousExposureForm]
          : [testSessionForm],
      goNext: async () => {
        const { Address } = testSessionForm.values
        if (
          Object.entries(Address).every(
            ([key, value]) => !!value || key === AddressFieldName.COMPLEMENT
          )
        ) {
          try {
            setIsFetching(true)
            if (!isWalkin) {
              const response = await apolloClient.query<
                ValidateTestLocationAddress,
                ValidateTestLocationAddressVariables
              >({
                query: VALIDATE_TEST_LOCATION_ADDRESS,
                variables: { Address: Address as AddressInput },
              })
              setTestLocationId(response.data.validateTestLocation.id)
            } else {
              const response = await apolloClient.query<
                ValidateTestLocationAddress,
                ValidateTestLocationAddressVariables
              >({
                query: VALIDATE_TEST_LOCATION_ADDRESS,
                variables: { Address: labAddress as AddressInput },
              })
              setTestLocationId(response.data.validateTestLocation.id)
            }
            if (!loggedInUserId) history.push(match.url.replace(formStep, FormStep.UserInformation))
            else history.push(match.url.replace(formStep, FormStep.Summary))
          } catch (error) {
            // ignore
          }
          setIsFetching(false)
        }
      },
    },
    ...(!loggedInUserId
      ? [
          {
            path: FormStep.UserInformation,
            forms: [userForm],
            goNext: () => history.push(match.url.replace(formStep, FormStep.Summary)),
          },
        ]
      : []),
    {
      path: FormStep.Summary,
      forms: [],
      goNext: () => history.push(match.url.replace(formStep, FormStep.TestSelection)),
    },
    {
      path: FormStep.TestSelection,
      forms: [selectTestForm],
      goNext: () => {
        void userForm.setFieldValue('optInMarketing', true)
        if (selectTestForm.values.testCount > INITIAL_APPLICANTS_COUNT) {
          history.push(match.url.replace(formStep, FormStep.AdditionalPatients))
          return
        }
        if (insuranceForm?.isInsurance) {
          history.push(match.url.replace(formStep, FormStep.DetailPayment))
        } else {
          history.push(match.url.replace(formStep, FormStep.Payment))
        }
      },
    },
    ...(selectTestForm.values.testCount > INITIAL_APPLICANTS_COUNT
      ? [
          {
            path: FormStep.AdditionalPatients,
            forms: [selectTestForm],
            goNext: () => {
              if (insuranceForm?.isInsurance) {
                history.push(match.url.replace(formStep, FormStep.DetailPayment))
              } else {
                history.push(match.url.replace(formStep, FormStep.Payment))
              }
            },
          },
        ]
      : []),
    ...(!insuranceForm?.isInsurance
      ? [
          {
            path: FormStep.Payment,
            forms: [],
            goNext: async () => {
              try {
                if (cardForm.errors.card) return
                const responseTS = await testSessionForm.submitForm()
                const response = await cardForm.submitForm()
                if (response && responseTS) {
                  history.push(match.url.replace(formStep, FormStep.ThankYou))
                }
              } catch {
                //
              }
            },
          },
        ]
      : [
          {
            path: FormStep.DetailPayment,
            forms: [],
            goNext: async () => {
              try {
                const response = await testSessionForm.submitForm()
                if (response) {
                  history.push(match.url.replace(formStep, FormStep.ThankYou))
                }
              } catch {
                //
              }
            },
          },
        ]),
    {
      path: FormStep.ThankYou,
      forms: [],
      goNext: () => {},
    },
  ]
  const stepIndex = steps.findIndex(({ path }) => path === formStep)
  const validateSteps = async () => {
    const validatations = steps
      .slice(0, stepIndex)
      .map(({ forms }) => Promise.all(forms.map(({ validateForm }) => validateForm())))
    const hasError = (await Promise.all(validatations)).some((validation) => {
      const errors = Array.isArray(validation) ? validation : [validation]
      return errors.some((error) => Object.values(error).length > 0)
    })

    if (hasError) {
      history.replace(`/${ROUTES.REQUEST_TEST_SESSION}/${FormStep.PretestSurvey}`)
    }
  }
  React.useEffect(() => {
    if (auth.isLoggedIn) {
      void userForm.setValues({
        ...auth.data.User,
      } as UserFormValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(auth.data.User)])

  React.useEffect(() => {
    void validateSteps()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formStep])

  const handleChangeWalkIn = () => {
    setIsWalkin(!isWalkin)
  }

  const PageWrapper = auth.isLoggedIn ? PersonalPageLayout : PageLayout
  const activeStep = steps[stepIndex]
  const isLoading =
    isFinishSessionLoading || isRequestSessionLoading || isFetching || cardForm.isSubmitting

  const anyTestInvalid = React.useMemo(
    () =>
      selectTestForm.values.tests.length > 0 &&
      selectTestForm.values.tests.some((test) => !test.priceId),
    [selectTestForm]
  )

  return (
    <PageWrapper hasCenteredContent={!isMobile} hasContentPadding={!isMobile}>
      <Stepper
        width="100%"
        maxWidth={pxToRem(560)}
        currentStepIndex={stepIndex}
        bottomContent={
          steps.length - 1 > stepIndex ? (
            <>
              <Button
                disabled={isLoading}
                onClick={() => {
                  void cardForm.setTouched({ card: false })
                  history.goBack()
                }}
                colorVariant="secondary"
              >
                Back
              </Button>
              <Button
                disabled={
                  anyTestInvalid ||
                  isLoading ||
                  addressNotCovered ||
                  activeStep?.forms.some((form) => !form.isValid) ||
                  (FormStep.UserInformation === activeStep?.path && !!emailAlreadyExist) ||
                  (FormStep.Payment === activeStep?.path &&
                    !cardForm.values.selectedCard &&
                    (!!cardForm.errors.card ||
                      !cardForm.touched.card ||
                      cardForm.values.card !== true)) ||
                  (auth.isLoggedIn &&
                    activeStep?.path === FormStep.Summary &&
                    insuranceForm?.insuranceId === null &&
                    insuranceForm?.isInsurance) ||
                  (!auth.isLoggedIn &&
                    activeStep?.path === FormStep.Summary &&
                    insuranceForm?.isInsurance &&
                    !insuranceFormNewUser.isValid)
                }
                isFetching={isLoading}
                onClick={activeStep?.goNext}
              >
                {activeStep?.path === FormStep.AdditionalPatients ||
                (activeStep?.path === FormStep.TestSelection &&
                  selectTestForm.values.testCount === INITIAL_APPLICANTS_COUNT)
                  ? 'Proceed to the payment'
                  : 'Next'}
              </Button>
            </>
          ) : undefined
        }
        appearance={isMobile ? 'Blank' : 'Card'}
      >
        <Stepper.Step
          title="Pretest form"
          render={() => (
            <PretestSurvey
              setLabAddress={setLabAddress}
              isWalkIn={isWalkin}
              handleChangeWalkIn={handleChangeWalkIn}
              symptomsForm={symptomsForm}
              testSessionForm={testSessionForm}
              userForm={userForm}
              previousExposureForm={previousExposureForm}
              selectTestForm={selectTestForm}
              setAddressNotCovered={setAddressNotCovered}
              addressNotCovered={addressNotCovered}
              resetInsuranceForm={() =>
                setInsuranceForm({
                  isInsurance: false,
                  insuranceId: null,
                })
              }
            />
          )}
        />
        {!loggedInUserId ? (
          <Stepper.Step
            title="User information"
            render={() => (
              <UserInformation
                userForm={userForm}
                emailAlreadyExist={emailAlreadyExist}
                setEmailAlreadyExist={(messageError) => setEmailAlreadyExist(messageError)}
              />
            )}
          />
        ) : undefined}
        <Stepper.Step
          title="Summary"
          render={() => (
            <Summary
              isWalkIn={isWalkin}
              userForm={userForm}
              symptomsForm={symptomsForm}
              testInfoForm={testSessionForm}
              previousExposureForm={previousExposureForm}
              setIsInsurance={(value: InsuranceProps) =>
                setInsuranceForm((prev) => ({ ...prev, ...value }))
              }
              insuranceForm={insuranceForm}
              insuranceFormNewUser={insuranceFormNewUser}
              isNewUser={!auth.isLoggedIn}
            />
          )}
        />
        <Stepper.Step
          title="Select Tests"
          render={() => (
            <TestSelection
              isWalkIn={isWalkin}
              testType={testSessionForm.values.testType}
              categoryType={testSessionForm.values.categoryType}
              testLocationId={testLocationId}
              selectTestForm={selectTestForm}
              testSessionForm={testSessionForm}
              isInsurance={insuranceForm?.isInsurance}
            />
          )}
        />
        {selectTestForm.values.testCount > INITIAL_APPLICANTS_COUNT ? (
          <Stepper.Step
            title="Additional Patients"
            render={() => (
              <AdditionalPatients
                selectTestForm={selectTestForm}
                testSessionForm={testSessionForm}
                userForm={userForm}
              />
            )}
          />
        ) : undefined}
        {!insuranceForm?.isInsurance ? (
          <Stepper.Step
            title="Payment"
            render={() => (
              <Payment
                userForm={userForm}
                cardForm={cardForm}
                testLocationId={testLocationId}
                selectTestForm={selectTestForm}
                couponForm={couponForm}
                toggleSubscribedMarketing={() =>
                  userForm.setFieldValue('optInMarketing', !userForm.values.optInMarketing)
                }
              />
            )}
          />
        ) : (
          <Stepper.Step
            title="Detail Payment"
            render={() => (
              <DetailPayment
                labAddress={labAddress}
                testSessionData={testSessionForm}
                testLocationId={testLocationId}
                selectTestForm={selectTestForm}
                userForm={userForm}
                insuranceFormNewUser={insuranceFormNewUser}
                isInsurance={insuranceForm?.isInsurance}
              />
            )}
          />
        )}

        <Stepper.Step
          title="Thank you"
          render={() => (
            <ThankYou
              labAddress={labAddress}
              isWalkIn={isWalkin}
              testSessionData={testSessionForm}
            />
          )}
        />
      </Stepper>
    </PageWrapper>
  )
}
